import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShieldCheckIcon, MinusIcon, PlusIcon } from 'lucide-react';
const PORTRAIT_IMG = 'https://cdn.magicpatterns.com/uploads/j2q1AHfEKx9M4PWF2acj8z/Imagen_1.jpg';
const MOCKUP_IMG = 'https://cdn.magicpatterns.com/uploads/a5GNQvpiyTfKdHL3uEfeVq/Imagen_2.jpg';
const COVER_IMG = 'https://cdn.magicpatterns.com/uploads/f6Cj579oe2vxk1CSGHa3Rj/Imagen_3.jpg';
const CHECKOUT_URL = 'https://pay.hotmart.com/K98488159N?off=dnutfmp1&checkoutMode=10';
const DEADLINE = new Date('2026-10-01T22:00:00-03:00').getTime();
const FAQ_ITEMS: {
  q: string;
  a: React.ReactNode;
}[] = [{
  q: '¿Cómo sé si esto es para mí?',
  a: 'Si sientes que das todo y aun así no te ven, es para ti.'
}, {
  q: '¿Es terapia o es un curso?',
  a: 'Es un método conductual con acompañamiento real, no solo contenido.'
}, {
  q: '¿Cuánto dura?',
  a: '30 días de programa, con acceso a las clases por 1 año.'
}, {
  q: '¿A qué hora son las sesiones en vivo?',
  a: <>
        <span className="block" data-id="element-0" data-mp="3ijpcgysnsv8p">
          1 y 29 de octubre a las 10:00 pm hora Argentina.
        </span>
        <span className="mt-2 block" data-id="element-1" data-mp="3mjg9vlcoy9m7">
          Referencia: 10:00 pm Chile · 9:00 pm Venezuela y Miami · 8:00 pm
          Colombia, Perú y Ecuador · 7:00 pm México.
        </span>
      </>
}, {
  q: '¿Qué pasa si no puedo asistir en vivo?',
  a: 'Las sesiones quedan grabadas para que las veas cuando puedas.'
}, {
  q: '¿Puedo pagar desde mi país?',
  a: 'Sí. Hotmart acepta tarjetas internacionales y métodos de pago locales según tu país.'
}, {
  q: '¿Tiene garantía?',
  a: 'Sí. Tienes 7 días de garantía. Si no es para ti, solicitas tu reembolso a través de Hotmart.'
}];
const INCLUDES = ['Sesión en vivo de diagnóstico: 1 de octubre', 'Sesión en vivo de cierre: 29 de octubre', 'Ambas sesiones quedan grabadas', '30 días de acompañamiento por WhatsApp', '+30 clases en Hotmart (acceso por 1 año)', 'Comunidad privada Mujer Elegida'];
const FOR_YOU = ['Das todo en tus relaciones y aun así no te eligen.', 'Eres la fuerte, la que resuelve, la que sostiene.', 'Atraes hombres que no se comprometen.', 'Quieres entender tu patrón, no solo sentirte mejor un día.'];
const NOT_FOR_YOU = ['Buscas una fórmula para "conquistar" a alguien en particular.', 'No estás dispuesta a revisar tu propia conducta.'];
type Remaining = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  finished: boolean;
};
function getRemaining(): Remaining {
  const diff = DEADLINE - Date.now();
  if (diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      finished: true
    };
  }
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor(totalSeconds % 86400 / 3600),
    minutes: Math.floor(totalSeconds % 3600 / 60),
    seconds: totalSeconds % 60,
    finished: false
  };
}
const pad = (n: number) => String(n).padStart(2, '0');
export function App() {
  const heroRef = useRef<HTMLElement | null>(null);
  const offerRef = useRef<HTMLElement | null>(null);
  const [remaining, setRemaining] = useState<Remaining>(getRemaining);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [pastHero, setPastHero] = useState(false);
  const [offerVisible, setOfferVisible] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => setRemaining(getRemaining()), 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const hero = heroRef.current;
    const offer = offerRef.current;
    if (!hero || !offer) return;
    const heroObserver = new IntersectionObserver(([entry]) => setPastHero(!entry.isIntersecting), {
      threshold: 0
    });
    const offerObserver = new IntersectionObserver(([entry]) => setOfferVisible(entry.isIntersecting), {
      threshold: 0.15
    });
    heroObserver.observe(hero);
    offerObserver.observe(offer);
    return () => {
      heroObserver.disconnect();
      offerObserver.disconnect();
    };
  }, []);
  const scrollToOffer = useCallback(() => {
    offerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);
  const showStickyBar = pastHero && !offerVisible;
  const countdownBoxes = useMemo(() => [{
    label: 'DÍAS',
    value: String(remaining.days)
  }, {
    label: 'HORAS',
    value: pad(remaining.hours)
  }, {
    label: 'MIN',
    value: pad(remaining.minutes)
  }, {
    label: 'SEG',
    value: pad(remaining.seconds)
  }], [remaining]);
  return <div className="w-full min-h-full bg-ink font-sans text-white" data-id="element-2" data-mp="zxale1wb5alh">
      <main className="mx-auto w-full max-w-[480px] pb-24 md:pb-0" data-id="element-3" data-mp="zzoei57fgcqe">
        {/* SECCIÓN 1 — HERO */}
        <section ref={heroRef} className="bg-brand px-6 py-16 text-center" aria-labelledby="hero-title" data-id="element-4" data-mp="3ko2riny8x0um">
          <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-5" data-mp="3glefk7e8jdtf">
            MSc Beatriz Espina · La Doc
          </p>
          <h1 id="hero-title" className="mt-6 font-display text-[34px] font-bold leading-[1.15]" data-id="element-6" data-mp="1w2lt4y7nu9ab">
            Eres un <span className="text-gold" data-id="element-7" data-mp="254v1pijdbsl2">10/10</span>. ¿Por qué nadie te
            ve?
          </h1>
          <p className="mx-auto mt-5 text-[17px] leading-[1.6] text-muted" data-id="element-8" data-mp="29w3bifom1fyr">
            El patrón de conducta que te hizo exitosa es el mismo que te está
            haciendo invisible.
          </p>
          <img src={PORTRAIT_IMG} alt="ELEGIDA — MSc Beatriz Espina, Estratega mental" className="mx-auto mt-8 w-[85%] rounded-2xl object-contain" data-id="element-9" data-mp="2jt7z8jy3tjho" />
          <button type="button" onClick={scrollToOffer} className="mt-9 h-14 w-full rounded-xl bg-gold text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-10" data-mp="2suxuk0v3x2kq">
            Quiero entender por qué
          </button>
          <p className="mt-4 text-[13px] leading-[1.6] text-muted" data-id="element-11" data-mp="2at1ud26clxvp">
            Programa de 30 días · Inicia el 1 de octubre
          </p>
        </section>

        {/* SECCIÓN 2 — CONTADOR */}
        <section className="border-y border-gold bg-ink px-6 py-16 text-center" aria-label="Cierre de inscripciones" data-id="element-12" data-mp="2kpeog15t2dqz">
          <p className="text-[17px] leading-[1.6] text-muted" data-id="element-13" data-mp="2fpiqaijshgjc">
            Las inscripciones cierran el 1 de octubre, al iniciar el diagnóstico
            en vivo.
          </p>

          {remaining.finished ? <p className="mt-8 font-display text-[28px] font-bold text-gold" data-id="element-14" data-mp="33go6iytn6au6">
              Inscripciones cerradas
            </p> : <div className="mt-8 grid grid-cols-4 gap-2" role="timer" data-id="element-15" data-mp="2yje9ax00guvs">
              {countdownBoxes.map(box => <div key={box.label} className="rounded-xl border border-gold/35 bg-white/[0.03] px-1 py-4" data-id="element-16" data-mp="3i1jw9chzhnoc">
                  <div className="font-display text-[30px] font-bold leading-none text-gold tabular-nums" data-id="element-17" data-mp="384fahds1vu82">
                    {box.value}
                  </div>
                  <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.12em] text-muted" data-id="element-18" data-mp="2zdeyqmrx6yxz">
                    {box.label}
                  </div>
                </div>)}
            </div>}

          <button type="button" onClick={scrollToOffer} className="mt-9 h-14 w-full rounded-xl bg-gold text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-19" data-mp="2pfqsp0ov650i">
            Reservar mi cupo
          </button>
        </section>

        {/* SECCIÓN 3 — ESPEJO */}
        <section className="bg-brand-up px-6 py-16" aria-labelledby="espejo" data-id="element-20" data-mp="2qx7k3xh6w32d">
          <h2 id="espejo" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-21" data-mp="2qzak9ac23ckt">
            Llegas sola. Otra vez.
          </h2>
          <div className="mt-6 space-y-3 text-[17px] leading-[1.6] text-muted" data-id="element-22" data-mp="2hxb0ia9npue6">
            <p data-id="element-23" data-mp="2d5jbi465fwq5">Preparada. Exitosa. Un 10/10 en todo, menos en esto.</p>
            <p data-id="element-24" data-mp="286ge95weuusq">No es que te falte algo.</p>
            <p data-id="element-25" data-mp="287hrnh0wftdm">Es que nadie te enseñó a ver tu propio patrón.</p>
          </div>
        </section>

        {/* SECCIÓN 4 — EL MECANISMO */}
        <section className="bg-ink px-6 py-16" aria-labelledby="mecanismo" data-id="element-26" data-mp="235se9uq7f3md">
          <div className="md:flex md:items-start md:gap-6" data-id="element-27" data-mp="1y7qtn5d4qw8s">
            <div className="md:flex-1" data-id="element-28" data-mp="1u6oueoj7qk6t">
              <h2 id="mecanismo" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-29" data-mp="1u7qetjm4cj17">
                No es tu belleza. Es tu conducta.
              </h2>
              <div className="mt-6 space-y-3 text-[17px] leading-[1.6] text-muted" data-id="element-30" data-mp="dskkwrvp0y67">
                <p data-id="element-31" data-mp="iqdr3kwzxs6h">
                  Años resolviendo, sosteniendo, demostrando… te volvieron
                  eficiente.
                </p>
                <p data-id="element-32" data-mp="3m6kstlvupfcm">
                  Y sin darte cuenta, esa misma fuerza apaga tu magnetismo.
                </p>
              </div>
              <blockquote className="mt-7 border-l-2 border-gold pl-4" data-id="element-33" data-mp="3r65xmcxpchx9">
                <p className="text-[17px] font-bold leading-[1.6] text-white" data-id="element-34" data-mp="3w3z3t5z09bxj">
                  Se llama conducta hipermasculinizada. Y se puede reeducar.
                </p>
              </blockquote>
            </div>
            <img src={MOCKUP_IMG} alt="MSc Beatriz Espina" className="mt-8 w-full rounded-2xl object-contain md:mt-0 md:w-[45%]" data-id="element-35" data-mp="33es07skp1w5f" />
          </div>
        </section>

        {/* SECCIÓN 5 — HISTORIA */}
        <section className="bg-brand px-6 py-16" aria-labelledby="historia" data-id="element-36" data-mp="2k680ngqf8b34">
          <h2 id="historia" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-37" data-mp="2k8b16up79mm3">
            A mí también me dejaron por un mensaje de texto.
          </h2>
          <div className="mt-6 space-y-3 text-[17px] leading-[1.6] text-muted" data-id="element-38" data-mp="2u2bdbgiwfs4b">
            <p data-id="element-39" data-mp="1wi6wek09zgti">10 años juntos. Me adapté para ser "la elegida".</p>
            <p data-id="element-40" data-mp="21exgrjj44ykq">
              Y aun así, me dejó por WhatsApp, mientras yo daba una charla sobre
              sexualidad.
            </p>
            <p data-id="element-41" data-mp="26diglhugdg8n">Ese día entendí el patrón que hoy enseño.</p>
          </div>
          <p className="mt-7 font-display text-[19px] italic text-gold" data-id="element-42" data-mp="2b6blc5px8okq">
            — MSc Beatriz Espina
          </p>
        </section>

        {/* SECCIÓN 6 — PARA QUIÉN ES */}
        <section className="bg-ink px-6 py-16" aria-labelledby="para-quien" data-id="element-43" data-mp="1iofpljdxib1h">
          <h2 id="para-quien" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-44" data-mp="3vz8qu8eybig2">
            ELEGIDA es para ti si…
          </h2>
          <ul className="mt-6 space-y-4" data-id="element-45" data-mp="3rw2mx7fba3cr">
            {FOR_YOU.map(item => <li key={item} className="flex gap-3" data-id="element-46" data-mp="3mwzhhlvf5v49">
                <span aria-hidden="true" className="text-[17px] text-gold" data-id="element-47" data-mp="3i094j7ewvfn2">
                  ✓
                </span>
                <span className="text-[17px] leading-[1.6] text-muted" data-id="element-48" data-mp="3d1o9dmeinm5h">
                  {item}
                </span>
              </li>)}
          </ul>

          <h3 className="mt-12 font-display text-[22px] font-bold leading-[1.3]" data-id="element-49" data-mp="3482ve5a6e8x4">
            No es para ti si…
          </h3>
          <ul className="mt-5 space-y-4" data-id="element-50" data-mp="2zaasyhbwn4eo">
            {NOT_FOR_YOU.map(item => <li key={item} className="flex gap-3" data-id="element-51" data-mp="2uaeq4ley1j0p">
                <span aria-hidden="true" className="text-[17px] text-[#8A8A8A]" data-id="element-52" data-mp="2uc99vwehb537">
                  ✕
                </span>
                <span className="text-[17px] leading-[1.6] text-[#8A8A8A]" data-id="element-53" data-mp="2fnprsmwfhbvw">
                  {item}
                </span>
              </li>)}
          </ul>

          <button type="button" onClick={scrollToOffer} className="mt-10 h-14 w-full rounded-xl bg-gold text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-54" data-mp="2kkg5x4oo9xeo">
            Sí, esto es para mí
          </button>
        </section>

        {/* SECCIÓN 7 — OFERTA */}
        <section ref={offerRef} id="oferta" className="bg-brand-up px-5 py-16" aria-labelledby="oferta-title" data-id="element-55" data-mp="2tlwhvgv4y7l0">
          <div className="rounded-2xl border-2 border-gold bg-ink/70 px-5 py-9 text-center" data-id="element-56" data-mp="3m39ynpcori70">
            <img src={COVER_IMG} alt="Todo lo que incluye ELEGIDA en laptop, tablet y celular" className="mx-auto w-full rounded-2xl object-contain" data-id="element-57" data-mp="1aojcyd0e8cos" />
            <h2 id="oferta-title" className="mt-7 font-display text-[28px] font-bold leading-[1.2]" data-id="element-58" data-mp="1ke2yxrjekmnv">
              ELEGIDA
            </h2>
            <p className="mt-2 text-[17px] leading-[1.6] text-muted" data-id="element-59" data-mp="1fhm288y50zz6">
              Programa conductual de 30 días
            </p>

            <ul className="mt-7 space-y-4 text-left" data-id="element-60" data-mp="mrdr3120rfu3">
              {INCLUDES.map(item => <li key={item} className="flex gap-3" data-id="element-61" data-mp="11oebejhvs8ij">
                  <span aria-hidden="true" className="text-[17px] text-gold" data-id="element-62" data-mp="11ls2ogj97myq">
                    ✓
                  </span>
                  <span className="text-[17px] leading-[1.6] text-muted" data-id="element-63" data-mp="2d2rsn5i4pppl">
                    {item}
                  </span>
                </li>)}
            </ul>

            <p className="mt-9 font-display text-[48px] font-bold leading-none text-gold" data-id="element-64" data-mp="2869qmde6o5gy">
              $97 USD
            </p>
            <p className="mt-3 text-[13px] leading-[1.6] text-muted" data-id="element-65" data-mp="mlefde97dq0m">
              Pago único · Garantía de 7 días
            </p>

            <a href={CHECKOUT_URL} className="mt-7 flex h-14 w-full items-center justify-center rounded-xl bg-gold text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-66" data-mp="qmqa1y6xp1fo">
              Quiero mi lugar en ELEGIDA
            </a>
            <p className="mt-4 text-[13px] leading-[1.6] text-muted" data-id="element-67" data-mp="1ef6qk4b0wlcv">
              🔒 Pago seguro procesado por Hotmart
            </p>
          </div>

          <div className="mt-8 flex gap-3" data-id="element-68" data-mp="1oagw320mrave">
            <ShieldCheckIcon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-69" data-mp="1o95r4yvg7x2p" />
            <p className="text-[15px] leading-[1.6] text-muted" data-id="element-70" data-mp="1tgdjrd8xajye">
              Garantía de 7 días. Si entras y sientes que no es para ti,
              solicitas tu reembolso a través de Hotmart. Sin preguntas.
            </p>
          </div>
        </section>

        {/* SECCIÓN 8 — PREGUNTAS FRECUENTES */}
        <section className="bg-ink px-6 py-16" aria-labelledby="faq-title" data-id="element-71" data-mp="2h67vfrhia2h2">
          <h2 id="faq-title" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-72" data-mp="2c6li7i1ogm89">
            Preguntas frecuentes
          </h2>
          <div className="mt-7 divide-y divide-white/10 border-y border-white/10" data-id="element-73" data-mp="27dsemxhy3jxr">
            {FAQ_ITEMS.map((item, index) => {
            const isOpen = openFaq === index;
            return <div key={item.q} data-id="element-74" data-mp="2zyaiplonkh0a" data-mpi={index}>
                  <h3 data-id="element-75" data-mp="2v4xuhcnvyc82">
                    <button type="button" onClick={() => setOpenFaq(isOpen ? null : index)} aria-expanded={isOpen} aria-controls={`faq-panel-${index}`} className="flex w-full items-start justify-between gap-4 py-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-76" data-mp="2v4el4kcaww5n">
                      <span className="text-[17px] font-semibold leading-[1.5] text-white" data-id="element-77" data-mp="ahpfclrrq3gv">
                        {item.q}
                      </span>
                      {isOpen ? <MinusIcon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-78" data-mp="j9nggkx2q6b" /> : <PlusIcon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-79" data-mp="1ux9gzrxsdfmg" />}
                    </button>
                  </h3>
                  {isOpen && <div id={`faq-panel-${index}`} className="pb-5 pr-9 text-[17px] leading-[1.6] text-muted" data-id="element-80" data-mp="3kcbrdy0g6t1f">
                      {item.a}
                    </div>}
                </div>;
          })}
          </div>
        </section>

        {/* SECCIÓN 9 — CIERRE */}
        <section className="bg-brand px-6 py-16" aria-labelledby="cierre" data-id="element-81" data-mp="6y76vp2i61a2">
          <h2 id="cierre" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-82" data-mp="2yohafzql20w">
            Deja de esperar que esto se resuelva solo.
          </h2>
          <p className="mt-6 text-[17px] leading-[1.6] text-muted" data-id="element-83" data-mp="by35t9wcf2ir">
            Si lo que has hecho hasta ahora funcionara, ya lo tendrías.
          </p>
          <button type="button" onClick={scrollToOffer} className="mt-9 h-14 w-full rounded-xl bg-gold px-3 text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-84" data-mp="2ir5ectj7elsv">
            Entrar a ELEGIDA — $97 USD
          </button>
          <p className="mt-4 text-[13px] leading-[1.6] text-muted" data-id="element-85" data-mp="18y6euncej7ae">
            Garantía de 7 días · Sesiones grabadas
          </p>
          <p className="mt-12 text-[12px] leading-[1.6] text-[#8A8A8A]" data-id="element-86" data-mp="1dy2i2kd9yuow">
            © 2026 Beatriz Espina · Todos los derechos reservados
          </p>
        </section>
      </main>

      {/* Sticky bar (solo mobile) */}
      <div className={`fixed inset-x-0 bottom-0 z-50 border-t border-gold bg-ink transition-[opacity,transform] duration-200 ease-out md:hidden ${showStickyBar ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'}`} aria-hidden={!showStickyBar} data-id="element-87" data-mp="1nws0qfczwj67">
        <div className="mx-auto flex w-full max-w-[480px] items-center justify-between gap-4 px-4 py-3" data-id="element-88" data-mp="innushie2ds6">
          <span className="text-[15px] font-semibold text-white" data-id="element-89" data-mp="dwfohuccv8j8">
            ELEGIDA · $97 USD
          </span>
          <button type="button" onClick={scrollToOffer} tabIndex={showStickyBar ? 0 : -1} className="h-11 shrink-0 rounded-xl bg-gold px-6 text-[14px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow] duration-150 ease-out hover:bg-goldbright focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-90" data-mp="8xbckvy1qwp7">
            Entrar
          </button>
        </div>
      </div>
    </div>;
}